import * as React from 'react'

import { PermitFieldType } from '~/client/graph'
import { getFieldTypeCaption } from '~/client/src/desktop/constants/formFieldsCaptionMap'
import InfoHint from '~/client/src/shared/components/InfoHint/InfoHint'
import { ListSelector } from '~/client/src/shared/components/ListSelector'
import {
  IListSelectorGroupConfig,
  IListSelectorGroupHeaderInfo,
  IListSelectorItem,
  IListSelectorSearchConfig,
} from '~/client/src/shared/components/ListSelector/types'
import PermitFieldIcon from '~/client/src/shared/components/PermitFieldIcon/PermitFieldIcon'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  addNewField(fieldType: PermitFieldType): void

  restrictedFields?: PermitFieldType[]
  disabledFields?: PermitFieldType[]
  children: React.ReactElement
}

interface IFieldItem extends IListSelectorItem {
  id: PermitFieldType
  groupKey: string
}

interface IFieldCategory {
  key: string
  parent?: string
  label: () => string
  description: () => string
  fields: PermitFieldType[]
}

const FIELD_CATEGORIES: IFieldCategory[] = [
  {
    key: 'struxHub',
    label: () => Localization.translator.struxHubFields,
    description: () => Localization.translator.struxHubFieldsDescription,
    fields: [],
  },
  {
    key: 'primary',
    parent: 'struxHub',
    label: () => Localization.translator.primaryWorkflowFields,
    description: () => Localization.translator.primaryWorkflowFieldsDescription,
    fields: [
      PermitFieldType.StartFinishDates,
      PermitFieldType.Location,
      PermitFieldType.Company,
      PermitFieldType.Requester,
      PermitFieldType.Assignee,
      PermitFieldType.Subscriber,
      PermitFieldType.Equipment,
      PermitFieldType.Material,
      PermitFieldType.MaterialResource,
      PermitFieldType.PrimaryActivity,
    ],
  },
  {
    key: 'secondary',
    parent: 'struxHub',
    label: () => Localization.translator.secondaryWorkflowFields,
    description: () =>
      Localization.translator.secondaryWorkflowFieldsDescription,
    fields: [
      PermitFieldType.LocationInformational,
      PermitFieldType.Activity,
      PermitFieldType.LinkedWorkflow,
      PermitFieldType.File,
      PermitFieldType.User,
      PermitFieldType.EquipmentInformational,
      PermitFieldType.MaterialInformational,
      PermitFieldType.MaterialResourceInformational,
      PermitFieldType.CompanyInformational,
    ],
  },
  {
    key: 'structure',
    label: () => Localization.translator.structureFields,
    description: () => Localization.translator.structureFieldsDescription,
    fields: [
      PermitFieldType.Section,
      PermitFieldType.Subsection,
      PermitFieldType.Divider,
    ],
  },
  {
    key: 'content',
    label: () => Localization.translator.contentFields,
    description: () => Localization.translator.contentFieldsDescription,
    fields: [
      PermitFieldType.Text,
      PermitFieldType.Instructions,
      PermitFieldType.InlineMedia,
    ],
  },
  {
    key: 'custom',
    label: () => Localization.translator.custom,
    description: () => Localization.translator.customFieldsDescription,
    fields: [
      PermitFieldType.InputText,
      PermitFieldType.InputTextArea,
      PermitFieldType.RichText,
      PermitFieldType.Question,
      PermitFieldType.Select,
      PermitFieldType.Checklist,
      PermitFieldType.Table,
      PermitFieldType.Signature,
      PermitFieldType.InputNumber,
      PermitFieldType.Measure,
      PermitFieldType.AdditionalDate,
      PermitFieldType.Time,
      PermitFieldType.PhoneNumber,
      PermitFieldType.Email,
    ],
  },
]

const CATEGORY_BY_KEY = new Map(FIELD_CATEGORIES.map(c => [c.key, c]))
const CATEGORY_ORDER = FIELD_CATEGORIES.map(c => c.key)

const NO_SELECTION = new Set<string>()

const GROUPING: IListSelectorGroupConfig<IFieldItem> = {
  options: [],
  defaultKey: null,
  buildGroupConfig: () => undefined,
  ungroupedConfig: {
    groupBy: item => item.groupKey,
    getGroupAncestors: key => {
      const parent = CATEGORY_BY_KEY.get(key)?.parent
      return parent ? [parent] : []
    },
    getGroupLabel: key => CATEGORY_BY_KEY.get(key)?.label() ?? key,
    sortGroups: (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b),
  },
}

const SEARCH: IListSelectorSearchConfig<IFieldItem> = {
  searchFn: (item, query) =>
    getFieldTypeCaption(item.id).toLowerCase().includes(query.trim()),
  autoFocus: true,
}

const getGroupHeaderProps = (
  group: IListSelectorGroupHeaderInfo<IFieldItem>,
) => ({
  trailing: (
    <InfoHint label={Localization.translator.aboutX(group.label)}>
      {CATEGORY_BY_KEY.get(group.key)?.description()}
    </InfoHint>
  ),
})

const renderItem = (
  item: IFieldItem,
  isSelected: boolean,
  onToggle: () => void,
  isDisabled: boolean,
) => (
  <ListSelector.Row
    isSelected={isSelected}
    onToggle={onToggle}
    disabled={isDisabled}
    testId={`form-field-option-${item.id}`}
    contentClassName="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:min-w-0"
  >
    <PermitFieldIcon fieldName={item.id} />
    <span className="tw:truncate">{getFieldTypeCaption(item.id)}</span>
  </ListSelector.Row>
)

export default function FormFieldsModal({
  addNewField,
  restrictedFields,
  disabledFields,
  children,
}: IProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const close = React.useCallback(() => setIsOpen(false), [])

  const items = React.useMemo(() => {
    const restricted = new Set(restrictedFields)
    return FIELD_CATEGORIES.flatMap(category =>
      category.fields
        .filter(field => !restricted.has(field))
        .map(field => ({ id: field, groupKey: category.key })),
    )
  }, [restrictedFields])

  const isItemDisabled = React.useMemo(() => {
    const disabled = new Set(disabledFields)
    return (item: IFieldItem) => disabled.has(item.id)
  }, [disabledFields])

  const handleItemToggle = React.useCallback(
    (id: string) => addNewField(id as PermitFieldType),
    [addNewField],
  )

  return (
    <ResponsiveSurface
      open={isOpen}
      onOpenChange={setIsOpen}
      title={Localization.translator.selectField}
      trigger={children}
      sizing="fill"
      width="picker"
      side="right"
      align="start"
    >
      <ListSelector.Root<IFieldItem>
        items={items}
        selectedIds={NO_SELECTION}
        selectionMode="single"
        onItemToggle={handleItemToggle}
        onClose={close}
        search={SEARCH}
        grouping={GROUPING}
        isItemDisabled={isItemDisabled}
        className="tw:flex-1 tw:min-h-0"
      >
        <ListSelector.Title
          title={Localization.translator.selectField}
          onClose={close}
        />
        <ListSelector.Toolbar />
        <ListSelector.Counter
          formatTotal={Localization.translator.xFieldsCount}
        />
        <ListSelector.List<IFieldItem>
          parentSizesToContent={true}
          renderItem={renderItem}
          getGroupHeaderProps={getGroupHeaderProps}
        />
      </ListSelector.Root>
    </ResponsiveSurface>
  )
}
